import type { TFunction } from 'i18next';

/**
 * Human-readable label for a course's bounded term ("season").
 *
 * A course term is the span between `termStart` and `termEnd` (ISO date strings,
 * e.g. "2026-09-01", or null). The label makes the time scope explicit wherever
 * a course / course option is shown.
 *
 * Shapes (in priority order):
 *  1. Missing term (either date absent)          → t('courses.seasonUnset')  e.g. "No term set"
 *  2. School-year span (Sep–Jun style: start     → t('courses.seasonShort', { a, b })
 *     month ≥ 7 and end in the *next* calendar       e.g. "26/27"
 *     year with end month ≤ 7)
 *  3. Both dates within one calendar year        → t('courses.season', { year })  e.g. "2026"
 *  4. Anything else (multi-year, odd spans)       → t('courses.seasonRange', { range })
 *     month-range via Intl.DateTimeFormat            e.g. "March 2026 – January 2027"
 *
 * Pure & side-effect free so it can be unit-tested without a React tree.
 *
 * @param termStart ISO date string or null
 * @param termEnd   ISO date string or null
 * @param t         i18next translate function (keys: courses.seasonShort/season/seasonRange/seasonUnset)
 * @param locale    BCP-47 locale for Intl month formatting (e.g. "en", "cs")
 */
export function seasonLabel(
  termStart: string | null | undefined,
  termEnd: string | null | undefined,
  t: TFunction,
  locale: string,
): string {
  if (!termStart || !termEnd) {
    return t('courses.seasonUnset');
  }

  // Parse at midday to avoid TZ edge flips around midnight.
  const start = new Date(`${termStart.slice(0, 10)}T12:00:00`);
  const end = new Date(`${termEnd.slice(0, 10)}T12:00:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return t('courses.seasonUnset');
  }

  // Inverted range (start after end) is not a meaningful season; fall back to the
  // unset label rather than rendering a backwards span like "2027 – 2026".
  if (start.getTime() > end.getTime()) {
    return t('courses.seasonUnset');
  }

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const startMonth = start.getMonth() + 1; // 1-12
  const endMonth = end.getMonth() + 1; // 1-12

  // 2. School-year shape: starts in the second half of a year (month ≥ 7) and
  //    ends in the following calendar year, early enough (month ≤ 7) to read as
  //    the same academic season. Rendered as the short "26/27" form.
  if (endYear === startYear + 1 && startMonth >= 7 && endMonth <= 7) {
    return t('courses.seasonShort', {
      a: String(startYear).slice(-2),
      b: String(endYear).slice(-2),
    });
  }

  // 3. Both dates within one calendar year → just the year.
  if (startYear === endYear) {
    return t('courses.season', { year: startYear });
  }

  // 4. Fallback: month-range using the requested locale.
  const fmt = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  return t('courses.seasonRange', {
    range: `${fmt.format(start)} – ${fmt.format(end)}`,
  });
}

/**
 * Season label plus an explicit day/month range, e.g. "26/27 · 1 Sep – 30 Jun".
 *
 * Reuses {@link seasonLabel} for the season suffix (school-year short form,
 * single year, or month-range) and appends a localized "day month" range built
 * from the ISO term bounds so a member can see the concrete coverage window at
 * the moment of choosing/paying. Falls back to the bare season label
 * (e.g. "No term set") when either bound is missing or unparseable.
 *
 * Pure & side-effect free so it can be unit-tested without a React tree.
 *
 * @param termStart ISO date string or null
 * @param termEnd   ISO date string or null
 * @param t         i18next translate function (adds key: courses.seasonTerm)
 * @param locale    BCP-47 locale for Intl day/month formatting (e.g. "en", "cs")
 */
export function seasonTermLabel(
  termStart: string | null | undefined,
  termEnd: string | null | undefined,
  t: TFunction,
  locale: string,
): string {
  const season = seasonLabel(termStart, termEnd, t, locale);
  if (!termStart || !termEnd) {
    return season;
  }

  const start = new Date(`${termStart.slice(0, 10)}T12:00:00`);
  const end = new Date(`${termEnd.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return season;
  }

  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  return t('courses.seasonTerm', {
    season,
    range: `${fmt.format(start)} – ${fmt.format(end)}`,
  });
}

export default seasonLabel;
